/**
 * WebSocket hook for real-time leaderboard updates.
 *
 * Connects to the WebSocket API Gateway endpoint, subscribes to leaderboard
 * updates for a given event, and invokes a callback when new data arrives.
 * Implements auto-reconnect with exponential backoff on disconnect.
 *
 * Requirements: 16.3 (real-time updates via WebSocket)
 */

import { useEffect, useRef, useCallback } from 'react';
import type { TeamLeaderboardEntry, IndividualLeaderboardEntry } from '@scoring/shared';

const WS_URL = import.meta.env.VITE_WS_URL ?? '';

/** Shape of the leaderboard update message pushed by the broadcaster Lambda. */
export interface LeaderboardUpdateMessage {
  eventId: string;
  team: TeamLeaderboardEntry[];
  individual: IndividualLeaderboardEntry[];
  lastUpdated: string | null;
}

export interface UseLeaderboardSocketOptions {
  /** The public event ID to subscribe to. */
  eventId: string | undefined;
  /** Called when a leaderboard update is received via WebSocket. */
  onUpdate: (message: LeaderboardUpdateMessage) => void;
  /** Whether the socket should be active. Defaults to true. */
  enabled?: boolean;
}

/** Maximum reconnect delay in milliseconds. */
const MAX_RECONNECT_DELAY_MS = 30_000;
/** Initial reconnect delay in milliseconds. */
const INITIAL_RECONNECT_DELAY_MS = 1_000;

/**
 * Connects to the WebSocket API and listens for leaderboard update messages.
 * Auto-reconnects with exponential backoff on disconnect.
 */
export function useLeaderboardSocket({
  eventId,
  onUpdate,
  enabled = true,
}: UseLeaderboardSocketOptions): void {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
  const onUpdateRef = useRef(onUpdate);

  // Keep the callback ref current without triggering reconnects.
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !eventId || !WS_URL) {
      cleanup();
      return;
    }

    let isCancelled = false;

    function connect(): void {
      if (isCancelled) return;

      const url = `${WS_URL}?eventId=${encodeURIComponent(eventId!)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.addEventListener('open', () => {
        // Reset backoff on successful connection.
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
      });

      ws.addEventListener('message', (event) => {
        try {
          // The broadcaster wraps the payload as { type, leaderboard }; older
          // shapes send it flat. Support both.
          const raw = JSON.parse(event.data as string) as
            | LeaderboardUpdateMessage
            | { type?: string; leaderboard?: LeaderboardUpdateMessage };
          const data = (
            'leaderboard' in raw && raw.leaderboard ? raw.leaderboard : raw
          ) as LeaderboardUpdateMessage;
          if (data.team && data.individual) {
            onUpdateRef.current(data);
          }
        } catch {
          // Ignore malformed messages.
        }
      });

      ws.addEventListener('close', () => {
        if (isCancelled) return;
        scheduleReconnect();
      });

      ws.addEventListener('error', () => {
        // The error event is always followed by a close event, so reconnect
        // logic is handled in the close handler.
      });
    }

    function scheduleReconnect(): void {
      if (isCancelled) return;
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connect();
      }, delay);
    }

    connect();

    return () => {
      isCancelled = true;
      cleanup();
    };
  }, [eventId, enabled, cleanup]);
}
